import type {
  PersistentStorage,
  Region,
} from "@karrotmarket-com/persistent-storage";
import type { HTMLAttributes, ReactElement } from "react";
import { forwardRef } from "react";

import { Box } from "@daangn/carotene";
import clsx from "clsx";
import { useDisclosure } from "~/hooks/useDisclosure";
import { useRootLoaderData } from "~/remix-lib";
import { FeatureFlag } from "~/services/FeatureFlag";
import LocationSelectModal from "~/services/gnb/common/LocationSelectModal";
import { HorizontalKeywords } from "../common/HorizontalKeywords";
import { SearchBarLayout } from "../common/SearchBarLayout";
import SelectRegionButton from "../common/SelectRegionButton";
import * as css from "../global/SearchBar.css";
import NeighbourhoodModal from "./NeighbourhoodModal";
import { SquareSelectRegionButton } from "./SquareSelectRegionButton";

export interface SearchBarProps extends HTMLAttributes<HTMLDivElement> {
  region: { id: string; name: string };
  keywordType: "popular" | "related";
  keywordComponentType?: "chip" | "text";
  keywords: { value: string; href: string }[];
  searchForm: ReactElement;
  size?: "medium" | "large";

  onSaveRegionToStorage: PersistentStorage<Region>["set"];
}

export const SearchBar = forwardRef<HTMLDivElement, SearchBarProps>(
  (
    {
      region,
      keywordType,
      keywords,
      className,
      searchForm,
      keywordComponentType,
      size = "medium",
      onSaveRegionToStorage,
      ...props
    },
    ref,
  ) => {
    const { isOpen, onOpen, onClose, onToggle } = useDisclosure();
    const rootData = useRootLoaderData();

    const showPopularKeywords = keywords.length > 0;

    return (
      <SearchBarLayout.Root
        className={clsx(
          className,
          css.container({
            showPopularKeywords,
          }),
        )}
        ref={ref}
        {...props}
      >
        <SearchBarLayout.Left>
          {rootData.user && rootData.currentUserLocation ? (
            <NeighbourhoodModal
              user={rootData.user}
              currentUserLocation={{
                userLocation: rootData.currentUserLocation.userLocation,
                userRegion: rootData.currentUserLocation.userRegion,
              }}
              trigger={
                <SelectRegionButton
                  size={size}
                  currentRegionName={
                    rootData.currentUserLocation.userLocation.name
                  }
                />
              }
              open={isOpen}
              onOpenChange={onToggle}
            />
          ) : (
            <LocationSelectModal
              trigger={
                <SelectRegionButton
                  size={size}
                  currentRegionName={region.name}
                />
              }
              onSaveRegionToStorage={onSaveRegionToStorage}
            />
          )}
        </SearchBarLayout.Left>
        <SearchBarLayout.Right>
          {searchForm}
          {rootData.user && rootData.currentUserLocation ? (
            <Box display={{ base: "block", medium: "none" }} paddingTop={4}>
              <NeighbourhoodModal
                user={rootData.user}
                currentUserLocation={{
                  userLocation: rootData.currentUserLocation.userLocation,
                  userRegion: rootData.currentUserLocation.userRegion,
                }}
                trigger={
                  <SquareSelectRegionButton
                    currentRegionName={
                      rootData.currentUserLocation.userLocation.name
                    }
                  />
                }
                open={isOpen}
                onOpenChange={onToggle}
              />
            </Box>
          ) : (
            <FeatureFlag.Root flag="yellow-knife">
              <FeatureFlag.Enabled>
                <Box display={{ base: "block", medium: "none" }} paddingTop={4}>
                  <LocationSelectModal
                    trigger={
                      <SquareSelectRegionButton
                        currentRegionName={region.name}
                      />
                    }
                    onSaveRegionToStorage={onSaveRegionToStorage}
                  />
                </Box>
              </FeatureFlag.Enabled>
            </FeatureFlag.Root>
          )}
          {/* FIXME: HorizontalKeywords와 SearchBar가 독립적으로 구현할 수 있는 방법은 없는지 고민해보기 */}
          {showPopularKeywords ? (
            <HorizontalKeywords
              keywordType={keywordType}
              keywords={keywords}
              keywordComponentType={keywordComponentType}
            />
          ) : null}
        </SearchBarLayout.Right>
      </SearchBarLayout.Root>
    );
  },
);

SearchBar.displayName = "SearchBar";
